import React, { useEffect, useRef, useState } from "react";
import { Provider, useSelector } from "react-redux";
import { store } from "./redux/store";
import AppRoutes from "./routes/AppRoutes";
import { ConfirmProvider } from "./components/Confirm/ConfirmProvider";
import ErrorBoundary from "./components/ErrorBoundary";
import PWAInstallPrompt from "./components/PWAInstallPrompt/PWAInstallPrompt";
import InstallPopup from "./components/InstallPopup/InstallPopup";
import PushNotificationPrompt from "./components/PushNotificationPrompt/PushNotificationPrompt";
import LoadingOverlay from "./components/LoadingOverlay/LoadingOverlay";
import SyncConflictModal from "./components/SyncConflictModal";
import Modal from "./components/Modal/Modal";
import { installAlertSound } from "./utils/alertSound";
import { setGlobalLoadingListener } from "./utils/api";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext";
import syncEngine from "./offline/SyncEngine";
import offlineStorage from "./offline/OfflineStorage";
import { installFetchInterceptor, uninstallFetchInterceptor } from "./offline/fetchInterceptor";
import { showToast } from "./utils/toast";
import { syncPushOnLogin } from "./utils/pushNotifications";
import "./App.scss";

function AppContent() {
  const [showGlobalLoading, setShowGlobalLoading] = useState(false);
  const pendingShowTimer = useRef(null);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [showOfflineInfo, setShowOfflineInfo] = useState(false);
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  useEffect(() => {
    installAlertSound();
  }, []);

  useEffect(() => {
    setGlobalLoadingListener((isLoading) => {
      if (isLoading) {
        if (!pendingShowTimer.current) {
          pendingShowTimer.current = setTimeout(() => {
            setShowGlobalLoading(true);
            pendingShowTimer.current = null;
          }, 180);
        }
        return;
      }

      if (pendingShowTimer.current) {
        clearTimeout(pendingShowTimer.current);
        pendingShowTimer.current = null;
      }
      setShowGlobalLoading(false);
    });

    return () => {
      if (pendingShowTimer.current) {
        clearTimeout(pendingShowTimer.current);
        pendingShowTimer.current = null;
      }
      setGlobalLoadingListener(null);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      const owner = user?.sub || user?.id || user?._id;
      if (owner) {
        offlineStorage.setOwner(owner);
        syncEngine.init();
        installFetchInterceptor();
      }
      // Re-register push if the user previously granted permission, so
      // reminders work without re-opening the app after login.
      syncPushOnLogin();
    } else {
      syncEngine.stop();
      offlineStorage.clearOwner();
      uninstallFetchInterceptor();
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    const goOnline = () => { setIsOnline(true); showToast('Back online — pending changes will sync', { type: 'success', duration: 4000 }); };
    const goOffline = () => { setIsOnline(false); showToast('You are offline — changes saved locally and will sync when reconnected', { type: 'warning', duration: 5000 }); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return (
    <>
      <AppRoutes />
      <PWAInstallPrompt />
      <InstallPopup />
      <PushNotificationPrompt />
      <LoadingOverlay visible={showGlobalLoading} />
      <SyncConflictModal />

      <Modal isOpen={showOfflineInfo} onClose={() => setShowOfflineInfo(false)} title="You're Offline" size="medium" className="modal--offline">
        <div className="offline-modal">
          <p className="offline-modal__text offline-modal__text--warning">
            This platform is designed for online use first. While offline mode is available, proceed with caution—some features may be delayed or require review after reconnecting.
          </p>
          <p className="offline-modal__text offline-modal__text--caution">
            You are offline—use this mode only for urgent edits. All other actions will sync later when online.
          </p>
          <p className="offline-modal__text offline-modal__text--info">
            You can create or add new data, but editing, updating, or deleting data is not available while offline.
          </p>
          <button className="offline-modal__dismiss" onClick={() => setShowOfflineInfo(false)}>Got it</button>
        </div>
      </Modal>

      {!isOnline && (
        <div
          className="offline-badge"
          role="status"
          aria-live="polite"
          onClick={() => setShowOfflineInfo(true)}
          title="Click for offline information"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
            <line x1="2" y1="2" x2="22" y2="22" />
          </svg>
          You are offline
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ThemeProvider>
          <LanguageProvider>
            <ConfirmProvider>
              <AppContent />
            </ConfirmProvider>
          </LanguageProvider>
        </ThemeProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
